import { useBoolean } from '@hover/blueprint';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

export interface UseIdleTimeoutOptions {
  /**
   * Time in seconds to wait once the user stops interacting before considering
   * them idle
   */
  duration: number;
  /**
   * Pause the idle timeout
   */
  pause?: boolean;
  /**
   * Optional target to check for interaction. When provided, the user will
   * always be considered idle unless they interact with the target element.
   */
  target?: RefObject<Element>;
}

const isCursorOverElement = (event: MouseEvent, element: Element) => {
  const { clientX, clientY } = event;

  const { left, top, right, bottom } = element.getBoundingClientRect();
  const { scrollX, scrollY } = window;

  return (
    clientX >= left + scrollX &&
    clientX <= right + scrollX &&
    clientY >= top + scrollY &&
    clientY <= bottom + scrollY
  );
};

/**
 * Determine whether user interaction is considered "idle" after the user stops
 * interacting for the specified duration.
 *
 * @remarks This currently only considers mouse movement, but could be extended
 * to include touch interactions.
 *
 * @param options
 *
 * @returns boolean representing whether user interaction is considered "idle"
 */
export const useIdleTimeout = ({
  duration,
  pause = false,
  target,
}: UseIdleTimeoutOptions) => {
  const [isIdle, { on: onIdle, off: onActive }] = useBoolean();
  const timeout = useRef<number>();

  useEffect(() => {
    const resetIdle = (event?: MouseEvent) => {
      if (
        target?.current &&
        event &&
        !isCursorOverElement(event, target.current)
      ) {
        onIdle();
        return;
      }

      onActive();

      window.clearTimeout(timeout.current);
      timeout.current = window.setTimeout(onIdle, duration * 1000);
    };

    if (pause) {
      window.clearTimeout(timeout.current);
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('click', resetIdle);
      return undefined;
    }

    resetIdle();
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('click', resetIdle);

    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('click', resetIdle);
      window.clearTimeout(timeout.current);
    };
  }, [duration, onActive, onIdle, pause, target]);

  return isIdle;
};
